/**
 * useAmendmentRoles — derive role flags for the Pay Operations section from the
 * current user's `userAddData` payload. The backend re-enforces every check;
 * these flags only control what affordances the UI shows.
 */

import { computed, type ComputedRef } from 'vue';
import { Roles } from '@/constants/roles';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';

const ADMIN_ROLES = [Roles.pay_operations_admin];

const AGENT_ROLES = [...ADMIN_ROLES, Roles.pay_operations_agent];

const SECTION_ROLES = [...AGENT_ROLES, Roles.pay_operations_viewer];

/** Roles allowed to update the manual PSP refund tracker (FQ-7). */
const REFUND_ROLES = [Roles.pay_operations_agent, Roles.pay_operations_admin];

function readRoles(): string[] {
  const data = getUserAddDataFromLocalStorage();
  return Array.isArray(data?.roles) ? data.roles : [];
}

export interface AmendmentRoles {
  roles: ComputedRef<string[]>;
  hasSectionAccess: ComputedRef<boolean>;
  hasAgentAccess: ComputedRef<boolean>;
  hasAdminAccess: ComputedRef<boolean>;
  canEditRefund: ComputedRef<boolean>;
}

export function useAmendmentRoles(): AmendmentRoles {
  const roles = computed(() => readRoles());
  const includesAny = (allowed: string[]) => roles.value.some((r) => allowed.includes(r as Roles));

  return {
    roles,
    hasSectionAccess: computed(() => includesAny(SECTION_ROLES)),
    hasAgentAccess: computed(() => includesAny(AGENT_ROLES)),
    hasAdminAccess: computed(() => includesAny(ADMIN_ROLES)),
    canEditRefund: computed(() => includesAny(REFUND_ROLES)),
  };
}
